import axios, { AxiosError, AxiosInstance } from 'axios';
import { JWTHelper } from '../auth/JWTHelper';
import { ErrorHelper } from './ErrorHelper';
import { UnauthorizedInterceptor } from './unauthorizedInterceptor';

const API_URL = process.env.REACT_APP_WM_API_URL as string;
const API_KEY = process.env.REACT_APP_WM_API_KEY as string;

export class Api {
    private token = localStorage.getItem('idToken') || JWTHelper.getTokenFromStorage() as string;
    private api: AxiosInstance;

    constructor(baseURL = API_URL) {
        this.api = axios.create({ baseURL: baseURL });
        this.api.interceptors.response.use(
            (response) => response,
            async (error: AxiosError<any>) => {
                if (error.response?.status === 401) {
                    const url = (error.config?.url ?? '').toString();
                    // ignorar 401 no login senão entra em loop infinito
                    const ignored401 = ['/login'].some((p) => url.startsWith(p));
                    if (!ignored401) {
                        UnauthorizedInterceptor.handleUnauthorized();
                    }
                }
                ErrorHelper.handle(error);
                return Promise.reject(error);
            }
        );
    }

    async fetch(route: string): Promise<unknown> {
        const response = await this.api.get(route, {
            headers: {
                Authorization: `Bearer ${this.token}`,
            },
        });

        return response.data;
    }

    async post<T>(route: string, data: T): Promise<unknown> {
        const response = await this.api.post(route, data, {
            headers: {
                Authorization: `Bearer ${this.token}`,
            },
        });

        return response.data;
    }

    async put<T>(route: string, data: T): Promise<unknown> {
        const response = await this.api.put(route, data, {
            headers: {
                Authorization: `Bearer ${this.token}`,
            },
        });

        return response.data;
    }

    async putWithResponse<T>(
        route: string,
        data: T
    ): Promise<{ status: number; data: unknown }> {
        const response = await this.api.put(route, data, {
            headers: {
                apiKey: API_KEY,
                Authorization: `Bearer ${this.token}`,
            },
        });

        return { status: response.status, data: response.data };
    }

    async delete(route: string): Promise<unknown> {
        const response = await this.api.delete(route, {
            headers: {
                Authorization: `Bearer ${this.token}`,
            },
        });

        return response.data;
    }
}
